import * as React from "react";

import { AsyncComponent } from "@impero-com/common/components/asyncComponent";
import {
    DataTable,
    DATA_TABLE_MODE_VIEWPORT,
    ColumnBuilder,
    ColumnOptions,
    FormattedView,
} from "@impero-com/common/components/dataTable";
import { TableFilter } from "@impero-com/common/components/filter";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { ProtectedLink } from "@impero-com/common/components/protectedLink";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { getErrorPageTitle } from "@impero-com/common/errorPages/sharedError";
import createIcon from "@impero-com/common/img/create-icon.svg";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as Cmp from "@impero-com/common/utils/comparison";
import {
    ArrayDataSource,
    DataExtractor,
    DataView,
    getAllExtractors,
    ExtractorExpander,
    forEach,
} from "@impero-com/common/utils/dataView";
import * as DV from "@impero-com/common/utils/dataView";
import {
    formatFileNameForExport,
    formatUserName,
} from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import {
    addFieldFilter,
    DataViewIndex,
    QueryResult,
    removeFieldFilter,
    SimpleAndFilter,
    SimpleFieldFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";
import * as XLSXUtils from "@impero-com/common/utils/xlsx";
import {
    actionMenuStatus,
    ActionSpec,
    Button,
    ContentWithoutBorder,
    emitNotification,
    InformationModal,
    PageSpinner,
} from "@impero-com/ui";
import { AxiosError } from "axios";
import { sortBy, memoize } from "lodash-es";
import memoizeOne from "memoize-one";
import { default as saveAs } from "save-as";

import {
    UserGroupsDeletionFailedDetails,
    UserGroupId,
    UserId,
    UserGroupsUserId,
    UserGroupViewListDto as UserGroupsOverviewWithUsers,
    AvailableActions,
    UserOverview,
    DeleteUserGroupsBody,
} from "api/generated/model";
import {
    deleteUserGroups,
    getUserGroups,
} from "api/generated/user-groups/user-groups";
import { TagList } from "components/tag/tagList";
import { Tag, TagCategory } from "components/tag/types";
import { PageStateManager } from "org/pageState/components/pageStateManager";
import { pageStateUserGroupsCodecs } from "org/pageState/convert/userGroups";
import { IdAndName } from "org/pageState/models";
import { PAGE_KEY } from "org/pageState/pageKey";
import { PageFilterAndColumns } from "org/pageState/types";
import { splitPageStateWithCallback } from "org/pageState/utils";
import * as theme from "org/user/list/theme.css";
import { downgradeExtractorType } from "utils/dataView";
import { STATUS_CODE_ERROR_OPERATION_FAILED } from "utils/errorCodes";
import { newExportMetadata } from "utils/export";

import { WebRoute } from "../../../routes";
import { ControlOperationFailedModal } from "../controlOperationFailed";

import { exportUserGroups } from "./groupListExport";
import {
    ShareableLinkProps,
    UserModuleListApplicationBar,
    USER_GROUPS_TAB,
} from "./listApplicationBar";
import { UserGroupListResponseWithUsers } from "./models";

function buildNameExtractor(
    groups: GroupView[]
): DataExtractor<GroupView, string, IdAndName<number, string>> {
    const mapIdToName: Record<number, string> = {};
    groups.forEach((group) => {
        mapIdToName[group.id] = group.name;
    });

    const formatValue = (group: GroupView) => group.name;

    return {
        id: "name",
        label: t("UserManagement_GroupName"),
        getValue: formatValue,
        compare: Cmp.map(formatValue, Cmp.string()),
        asString: formatValue,
        customFilterTransformer: {
            getValue: (group) => ({ id: group.id, name: group.name }),
            asString: (idAndName: IdAndName<number, string>) =>
                mapIdToName[idAndName.id] || idAndName.name,
        },
    };
}

function buildMembersExtractor(): DataExtractor<GroupView, number> {
    const extractValue = (group: GroupView) => group.users.length;
    const formatValue = (members: number) => members.toString();
    const extractAndFormatValue = (group: GroupView) =>
        formatValue(extractValue(group));

    return {
        id: "members",
        label: t("UserGroup_Members"),
        getValue: extractValue,
        compare: Cmp.map(extractValue, Cmp.primitive),
        asString: extractAndFormatValue,
        customFilterTransformer: {
            getValue: extractValue,
            asString: formatValue,
        },
    };
}

function buildCreatedByExtractor(
    users: UserOverview[]
): DataExtractor<GroupView, string, IdAndName<number, string>> {
    const mapIdToUser: Record<number, UserOverview> = {};
    users.forEach((user) => {
        mapIdToUser[user.id] = user;
    });

    const formatValue = (group: GroupView) => group.createdBy;

    return {
        id: "createdBy",
        label: t("UserGroup_CreatedBy"),
        getValue: formatValue,
        compare: Cmp.map(formatValue, Cmp.string()),
        asString: formatValue,
        customFilterTransformer: {
            getValue: (group) => ({
                id: group.createdByUserId || 0,
                name: group.createdBy,
            }),
            asString: (idAndName: IdAndName<number, string>) => {
                const user = mapIdToUser[idAndName.id];
                return user ? formatUserName(user) : idAndName.name;
            },
        },
    };
}

interface UserGroupListProps {
    ajaxManager: AjaxManager;
    availableActions: AvailableActions;
}

interface UserGroupListState {
    dialog: { type: "DELETE"; userGroupIds: Array<number> } | undefined;
    errorModal: ErrorModalState | undefined;
}

interface ErrorModalState {
    type: "PendingAssignments";
    userGroupIds: Array<number>;
}

interface ExpandedRow<E> {
    dataView: DV.DataView<E>;
    columnBuilders: { [extractorId: string]: ColumnBuilder<E, any> };
}

interface UserView {
    id: UserId;
    groupUserId: UserGroupsUserId;
    name: string;
    email: string;
    tags: Array<Tag>;
    tagCategories: Array<TagCategory>;
    hasAccess: boolean;
}

interface GroupView {
    id: UserGroupId;
    name: string;
    createdBy: string;
    createdByUserId: number | null;
    users: Array<UserView>;
}

class UserGroupList extends React.PureComponent<
    UserGroupListProps,
    UserGroupListState
> {
    private asyncComponent =
        React.createRef<AsyncComponent<UserGroupListResponseWithUsers>>();

    state: UserGroupListState = {
        dialog: undefined,
        errorModal: undefined,
    };

    private memoizeDataView: (
        groupsWithUsers: UserGroupsOverviewWithUsers
    ) => [DataView<GroupView>, DataViewIndex<GroupView>] = memoizeOne(
        (groupsWithUsers: UserGroupsOverviewWithUsers) => {
            const { content: userGroups, linkedResources } = groupsWithUsers;

            const filteredUserGroups = userGroups.filter(
                (group) => group.actions.admin || group.actions.edit
            );

            const userGroupViews: GroupView[] = filteredUserGroups.map(
                (dto) => {
                    const group = dto.resource;
                    const createdBy =
                        group.createdBy &&
                        linkedResources.users.find(
                            (user) => user.resource.id === group.createdBy
                        )?.resource;
                    return {
                        id: group.id,
                        name: group.name,
                        createdBy: createdBy ? formatUserName(createdBy) : "",
                        createdByUserId: group.createdBy ?? null,
                        users: sortBy(
                            group.userGroupUsers.map((groupUser) => {
                                const user = linkedResources.users.find(
                                    (u) => u.resource.id === groupUser.userId
                                )!.resource;
                                const tags = groupUser.userTagIds.map(
                                    (tagId) =>
                                        linkedResources.userTags.find(
                                            (t) => t.id === tagId
                                        )!
                                );
                                const tagCategoryIds = tags.map(
                                    (tag) => tag.tagCategoryId
                                );
                                const tagCategories = tagCategoryIds.map(
                                    (catId) =>
                                        linkedResources.userTagCategories.find(
                                            (c) => c.id === catId
                                        )!
                                );
                                return {
                                    id: groupUser.userId,
                                    groupUserId: groupUser.id,
                                    name: formatUserName(user),
                                    email: user.email,
                                    tags,
                                    tagCategories,
                                    hasAccess: linkedResources.users.find(
                                        (dto) =>
                                            dto.resource.id === groupUser.userId
                                    )!.actions.edit,
                                };
                            }),
                            ["name"]
                        ),
                    };
                }
            );

            const extractors: Array<DataExtractor<GroupView, unknown>> = [
                downgradeExtractorType(buildNameExtractor(userGroupViews)),
                downgradeExtractorType(buildMembersExtractor()),
                downgradeExtractorType(
                    buildCreatedByExtractor(
                        linkedResources.users.map((dto) => dto.resource)
                    )
                ),
            ];

            const dataView: DataView<GroupView> = {
                dataSource: new ArrayDataSource(userGroupViews, (group) =>
                    group.id.toString()
                ),
                extractorGroups: [
                    {
                        id: "standard",
                        label: "Standard",
                        extractors,
                    },
                ],
                sorters: [],
                defaultCompareItems: Cmp.map(
                    (group) => group.id,
                    Cmp.primitive
                ),
            };

            const expanders: Array<ExtractorExpander<any, any>> = [];

            return [
                dataView,
                new DataViewIndex(dataView, undefined, expanders),
            ];
        }
    );

    private buildQueryResult = memoizeOne(
        (
            dataView: DataView<GroupView>,
            dataViewIndex: DataViewIndex<GroupView>,
            filter: SimpleAndFilter | null
        ): QueryResult<GroupView> => {
            if (filter) {
                return dataViewIndex.find(filter);
            } else {
                return {
                    dataView,
                    orResults: [],
                    excludedItemIds: null,
                };
            }
        }
    );

    private getActions(
        availableActions: AvailableActions,
        forceDisable: boolean
    ): ActionSpec[] {
        return [
            {
                id: "create-group",
                label: t("CreateUserGroup_CreateTitle"),
                icon: { svg: createIcon },
                link: { to: `${WebRoute.USER_GROUPS}/new` },
                status: actionMenuStatus(
                    !forceDisable && availableActions.user.userGroup.create,
                    t("UserGroup_Create_Forbidden")
                ),
            },
        ];
    }

    private renderDialog(_groupsWithUsers: UserGroupListResponseWithUsers) {
        const { dialog } = this.state;
        if (!dialog) {
            return null;
        }

        const requestBody: DeleteUserGroupsBody = {
            userGroupIds: dialog.userGroupIds,
        };
        const deleteGroupsTitle = t("User_Groups_Delete_Title", {
            count: dialog.userGroupIds.length,
        });
        return (
            <InformationModal
                open
                onClose={() => this.setState({ dialog: undefined })}
                type="error"
                icon="delete"
                title={deleteGroupsTitle}
                footer={
                    <>
                        <Button
                            onClick={() => this.setState({ dialog: undefined })}
                        >
                            {t("Cancel")}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                deleteUserGroups(requestBody)
                                    .then(() => {
                                        const description = t(
                                            "User_Groups_Delete_Message_Deleted",
                                            {
                                                count: dialog.userGroupIds
                                                    .length,
                                            }
                                        );
                                        emitNotification(
                                            "success",
                                            description
                                        );
                                        this.setState({ dialog: undefined });
                                        this.asyncComponent.current?.reload();
                                    })
                                    .catch(
                                        (
                                            err: AxiosError<UserGroupsDeletionFailedDetails>
                                        ) => {
                                            if (
                                                err.response !== undefined &&
                                                err.response.status ===
                                                    STATUS_CODE_ERROR_OPERATION_FAILED
                                            ) {
                                                const details =
                                                    err.response.data;
                                                switch (details.type) {
                                                    case "PendingAssignments": {
                                                        this.setState({
                                                            errorModal: {
                                                                type: "PendingAssignments",
                                                                userGroupIds:
                                                                    details.data
                                                                        .userGroupIds,
                                                            },
                                                        });
                                                        break;
                                                    }
                                                    default:
                                                        assertNever(
                                                            details.type
                                                        );
                                                }
                                            } else {
                                                emitNotification(
                                                    "error",
                                                    err.message
                                                );
                                            }
                                            this.setState({
                                                dialog: undefined,
                                            });
                                        }
                                    );
                            }}
                        >
                            {t("Delete")}
                        </Button>
                    </>
                }
            >
                <p>
                    {t("User_Groups_Delete_Message_Body", {
                        count: dialog.userGroupIds.length,
                    })}
                </p>
            </InformationModal>
        );
    }

    private renderErrorModal(
        userGroupList: UserGroupListResponseWithUsers
    ): React.ReactNode | null {
        const errorModalState = this.state.errorModal;

        if (!errorModalState) {
            return null;
        }

        switch (errorModalState.type) {
            case "PendingAssignments": {
                const getUserGroupNameById = (id: number): string => {
                    const ug = userGroupList.data.content.find(
                        (dto) => dto.resource.id === id
                    );
                    if (ug !== undefined) {
                        return ug.resource.name;
                    } else {
                        throw new Error(`UserGroup with id ${id} not found`);
                    }
                };
                const problematicUserGroupNames =
                    errorModalState.userGroupIds.map(getUserGroupNameById);
                const onClickOk = () =>
                    this.setState({ errorModal: undefined });
                return (
                    <CannotDeleteUserGroupsModal
                        problematicUserGroupNames={problematicUserGroupNames}
                        onClickOk={onClickOk}
                    />
                );
            }
            default:
                return assertNever(errorModalState.type);
        }
    }

    render = () => {
        const { ajaxManager, availableActions } = this.props;

        const renderError = (code: number | undefined) => (
            <UserModuleListApplicationBar
                actions={this.getActions(availableActions, true)}
                currentTabId={USER_GROUPS_TAB}
                shareableLink={{
                    state: "inactive",
                    reason:
                        code === 403
                            ? t("ShareableLink_CreateNew_Forbidden")
                            : getErrorPageTitle(code),
                }}
            >
                {code === 403 ? (
                    <NoAccessCard />
                ) : (
                    <LoggedInHTTPError code={code} />
                )}
            </UserModuleListApplicationBar>
        );
        const renderLoading = () => <PageSpinner />;

        return (
            <AsyncComponent<UserGroupListResponseWithUsers>
                ajaxManager={ajaxManager}
                ref={this.asyncComponent}
                promise={() =>
                    getUserGroups({ format: "WithUsers" }).then((response) => {
                        if (response.data.type !== "WithUsers") {
                            throw new Error("Expected WithUsers response type");
                        }
                        return {
                            ...response,
                            data: response.data,
                        };
                    })
                }
                key="userGroupList"
                _overrideErrorHandlers={[403]}
                renderLoading={renderLoading}
                renderError={renderError}
                renderContent={(
                    groupsWithUsers: UserGroupListResponseWithUsers
                ) => {
                    return (
                        <PageStateManager<"UserGroups", PageFilterAndColumns>
                            pageKey={PAGE_KEY.userGroups()}
                            renderLoading={renderLoading}
                            renderError={renderError}
                            codecs={pageStateUserGroupsCodecs}
                            renderContent={({
                                pageState,
                                onPageStateChange,
                                onCreateShareableLink,
                            }) => {
                                const { filter, onFilterChange } =
                                    splitPageStateWithCallback(
                                        pageState,
                                        onPageStateChange
                                    );
                                const { data } = groupsWithUsers;
                                const deletableIds: Array<string> = data.content
                                    .filter((dto) => dto.actions.delete)
                                    .map((dto) => dto.resource.id.toString());
                                const [dataView, dataViewIndex] =
                                    this.memoizeDataView(data);
                                const queryResult = this.buildQueryResult(
                                    dataView,
                                    dataViewIndex,
                                    filter
                                );
                                const columnBuilders: {
                                    [extractor: string]: ColumnBuilder<
                                        GroupView,
                                        any
                                    >;
                                } = {
                                    name: (
                                        extractor
                                    ): ColumnOptions<GroupView> => ({
                                        getFormattedValue: (group) => (
                                            <a
                                                href={`${WebRoute.USER_GROUPS}/${group.id}#members`}
                                            >
                                                {extractor.getValue(group)}
                                            </a>
                                        ),
                                    }),
                                    members: (): ColumnOptions<GroupView> => ({
                                        layout: { fixedWidth: 150 },
                                    }),
                                    createdBy:
                                        (): ColumnOptions<GroupView> => ({
                                            layout: { fixedWidth: 300 },
                                        }),
                                };
                                const actionsAreEmpty = !data.content.some(
                                    (dto) =>
                                        dto.actions.admin || dto.actions.edit
                                );
                                const hasInsufficientAccess =
                                    actionsAreEmpty &&
                                    !availableActions.user.userGroup.create;

                                const shareableLinkProps: ShareableLinkProps =
                                    hasInsufficientAccess
                                        ? {
                                              state: "inactive",
                                              reason: t(
                                                  "ShareableLink_CreateNew_Forbidden"
                                              ),
                                          }
                                        : {
                                              state: "active",
                                              onCreate: onCreateShareableLink,
                                          };

                                return (
                                    <UserModuleListApplicationBar
                                        actions={this.getActions(
                                            availableActions,
                                            false
                                        )}
                                        currentTabId={USER_GROUPS_TAB}
                                        shareableLink={shareableLinkProps}
                                    >
                                        {hasInsufficientAccess ? (
                                            <NoAccessCard />
                                        ) : (
                                            <ContentWithoutBorder
                                                className={theme.whiteBg}
                                            >
                                                <TableFilter<GroupView>
                                                    id="filter"
                                                    queryResult={queryResult}
                                                    dataViewIndex={
                                                        dataViewIndex
                                                    }
                                                    filter={filter}
                                                    extractorRefs={getAllExtractors(
                                                        dataView
                                                    ).map((ext) => ({
                                                        extractorId: ext.id,
                                                    }))}
                                                    onAddFilter={this.addFilter(
                                                        filter,
                                                        onFilterChange
                                                    )}
                                                    onRemoveFilter={this.removeFilter(
                                                        filter,
                                                        onFilterChange
                                                    )}
                                                    onResetFilter={this.resetFilter(
                                                        filter,
                                                        onFilterChange
                                                    )}
                                                />
                                                <DataTable
                                                    id="user-group-list"
                                                    mode={
                                                        DATA_TABLE_MODE_VIEWPORT
                                                    }
                                                    selectionHandler={{
                                                        tools: [
                                                            {
                                                                isEnabled: (
                                                                    _formattedView,
                                                                    selectedIds,
                                                                    _visibleColumns
                                                                ) =>
                                                                    canDeleteUserGroups(
                                                                        selectedIds,
                                                                        deletableIds
                                                                    ),
                                                                type: "Text",
                                                                label: t(
                                                                    "User_Groups_Delete_Title_Action"
                                                                ),
                                                                onCommand:
                                                                    this
                                                                        .deleteUserGroups,
                                                            },
                                                            {
                                                                type: "Text",
                                                                label: t(
                                                                    "User_List_Action_Export"
                                                                ),
                                                                onCommand: (
                                                                    onSelectionActionDone,
                                                                    formattedView,
                                                                    selectedIds,
                                                                    _visibleColumnIds
                                                                ) =>
                                                                    this.onExport(
                                                                        onSelectionActionDone,
                                                                        formattedView,
                                                                        selectedIds
                                                                    ),
                                                            },
                                                        ],
                                                    }}
                                                    dataView={
                                                        queryResult.dataView
                                                    }
                                                    columnBuilders={
                                                        columnBuilders
                                                    }
                                                    expandedRowRender={
                                                        this.renderExpandedRow
                                                    }
                                                    expandedRowClassName={() =>
                                                        theme.expandedRow
                                                    }
                                                />
                                            </ContentWithoutBorder>
                                        )}
                                        {this.renderDialog(groupsWithUsers)}
                                        {this.renderErrorModal(groupsWithUsers)}
                                    </UserModuleListApplicationBar>
                                );
                            }}
                        />
                    );
                }}
            />
        );
    };

    private deleteUserGroups = (
        _onSelectionActionDone: () => void,
        _formattedView: FormattedView<GroupView>,
        selectedIds: Array<string>,
        _visibleColumnIds: Set<string>
    ) => {
        this.setState({
            dialog: {
                type: "DELETE",
                userGroupIds: selectedIds.map((x) => Number(x)),
            },
        });
    };

    private onExpand: (group: GroupView) => ExpandedRow<UserView> = memoize(
        (
            group: GroupView /* XXX If you add arguments, please define a memoization strategy */
        ): ExpandedRow<UserView> => {
            const stringCompare = Cmp.string();
            return {
                dataView: {
                    dataSource: new ArrayDataSource<UserView>(
                        group.users,
                        (user) => user.groupUserId.toString()
                    ),
                    extractorGroups: [
                        {
                            id: "standard",
                            label: "Standard",
                            extractors: [
                                {
                                    id: "name",
                                    label: t("User_List_Column_Name"),
                                    getValue: (user) => user.name,
                                    compare: Cmp.map(
                                        (user) => user.name,
                                        stringCompare
                                    ),
                                    asString: (user) => user.name,
                                },
                                {
                                    id: "email",
                                    label: t("User_List_Column_Email"),
                                    getValue: (user) => user.email,
                                    compare: Cmp.map(
                                        (user) => user.email,
                                        stringCompare
                                    ),
                                    asString: (user) => user.email,
                                },
                                {
                                    id: "tags",
                                    label: t("ViewControl_Tags"),
                                    getValue: (user) => user.tags,
                                    compare: Cmp.map(
                                        (user) => user.tags.length,
                                        Cmp.primitive
                                    ),
                                    asString: (user) =>
                                        user.tags.length.toString(),
                                },
                            ],
                        },
                    ],
                    sorters: [],
                    defaultCompareItems: Cmp.map(
                        (user) => user.name,
                        stringCompare
                    ),
                },
                columnBuilders: {
                    name: (extractor): ColumnOptions<UserView> => ({
                        getFormattedValue: (user) => (
                            <ProtectedLink
                                to={`${WebRoute.USERS}/${user.id}`}
                                hasAccess={user.hasAccess}
                            >
                                {extractor.getValue(user)}
                            </ProtectedLink>
                        ),
                        layout: { fixedWidth: 300 },
                    }),
                    email: (_extractor): ColumnOptions<UserView> => ({
                        layout: { fixedWidth: 300 },
                    }),
                    tags: (_extractor): ColumnOptions<UserView> => ({
                        getFormattedValue: (user) => {
                            const { tags, tagCategories } = user;
                            return (
                                <TagList
                                    mode="HOVER"
                                    tags={tags}
                                    tagCategories={tagCategories}
                                />
                            );
                        },
                    }),
                },
            };
        }
    );

    private renderExpandedRow = (
        item: GroupView,
        _index: number,
        _indent: number,
        _expanded: boolean
    ): React.ReactNode => {
        const expanded = this.onExpand(item);
        const headers = expanded.dataView.extractorGroups.map((group) => {
            return group.extractors.map((extractor) => {
                const builder = expanded.columnBuilders[extractor.id];
                let style;
                if (builder) {
                    const columnBuilder = builder(extractor);
                    style = columnBuilder.layout && {
                        width: `${columnBuilder.layout.fixedWidth}px`,
                    };
                }
                return (
                    <th
                        key={`${group.id}_${extractor.id}`}
                        style={style}
                        className={theme.expandedHeader}
                    >
                        {extractor.label}
                    </th>
                );
            });
        });
        const rows: Array<React.ReactNode> = [];
        forEach(expanded.dataView.dataSource.iter(), (row) => {
            const rowId = expanded.dataView.dataSource.getId(row);
            rows.push(
                <tr key={`row_${rowId}`}>
                    {expanded.dataView.extractorGroups.map((group) => {
                        return group.extractors.map((extractor) => {
                            const builder =
                                expanded.columnBuilders[extractor.id];
                            let value: React.ReactNode;
                            let style;
                            if (builder) {
                                const columnBuilder = builder(extractor);
                                const format = columnBuilder.getFormattedValue;
                                if (format) {
                                    value = format(row);
                                }
                                style = columnBuilder.layout && {
                                    width: `${columnBuilder.layout.fixedWidth}px`,
                                };
                            }
                            if (!value) {
                                value = extractor.asString(row);
                            }
                            return (
                                <td
                                    key={`${group.id}_${extractor.id}`}
                                    style={style}
                                >
                                    {value}
                                </td>
                            );
                        });
                    })}
                </tr>
            );
        });
        return (
            <table className={theme.expandedTable}>
                <thead>
                    <tr>{headers}</tr>
                </thead>
                <tbody>{rows}</tbody>
            </table>
        );
    };

    private onExport = (
        onSelectionActionDone: () => void,
        formattedView: FormattedView<GroupView>,
        selectedIds: Array<string>
    ) => {
        const exportableArray = exportUserGroups(
            formattedView.dataView,
            selectedIds
        );

        const metadata = newExportMetadata();
        const worksheet = XLSXUtils.tableToWorksheet(
            exportableArray,
            undefined,
            metadata
        );
        const workbook = XLSXUtils.workbookWithSingleWorksheet(
            "User Groups",
            worksheet
        );
        const data = XLSXUtils.workbookToBinary(workbook);

        const blob = new Blob([data], {
            type: "application/vnc.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, formatFileNameForExport("UserGroups", "xlsx"));

        onSelectionActionDone();
    };

    private addFilter =
        (
            oldFilter: SimpleAndFilter | null,
            onFilterChanged:
                | ((filter: SimpleAndFilter | null) => void)
                | undefined
        ) =>
        (extractorId: string, filter: SimpleFieldFilter, excluded: boolean) => {
            if (onFilterChanged) {
                onFilterChanged(
                    addFieldFilter(extractorId, filter, excluded, oldFilter)
                );
            }
        };

    private removeFilter =
        (
            oldFilter: SimpleAndFilter | null,
            onFilterChanged:
                | ((filter: SimpleAndFilter | null) => void)
                | undefined
        ) =>
        (extractorId: string, filter: SimpleFieldFilter, excluded: boolean) => {
            if (onFilterChanged) {
                onFilterChanged(
                    oldFilter &&
                        removeFieldFilter(
                            extractorId,
                            filter,
                            excluded,
                            oldFilter
                        )
                );
            }
        };

    private resetFilter =
        (
            _oldFilter: SimpleAndFilter | null,
            onFilterChanged:
                | ((filter: SimpleAndFilter | null) => void)
                | undefined
        ) =>
        () => {
            if (onFilterChanged) {
                onFilterChanged(null);
            }
        };
}

const canDeleteUserGroups = (
    selectedIds: Array<string>,
    deletableIds: Array<string>
): boolean => {
    return selectedIds.every((id) => deletableIds.includes(id));
};

interface CannotDeleteUserGroupsModalProps {
    problematicUserGroupNames: Array<string>;
    onClickOk: () => void;
}

function CannotDeleteUserGroupsModal(props: CannotDeleteUserGroupsModalProps) {
    const { problematicUserGroupNames, onClickOk } = props;
    const count = problematicUserGroupNames.length;
    const message = (userGroupName: string) => userGroupName;

    return (
        <ControlOperationFailedModal<string>
            title={t("User_Groups_Delete_Failed_Title", { count })}
            header={t("User_Groups_Delete_Failed_PendingAssignments_Header", {
                count,
            })}
            body={t("User_Groups_Delete_Failed_PendingAssignments_Body", {
                count,
            })}
            descriptionSorter={Cmp.primitive}
            onClickOk={onClickOk}
            message={message}
            descriptions={problematicUserGroupNames}
        />
    );
}

export { UserGroupList, type GroupView };
